import {
  createResponsibility,
  deleteResponsibility,
  getResponsibilityAttachments,
} from "./responsibilityAPI";
import { attachResponsibilityToRole } from "./roleAPI";

export const CREATE_ATTACH_ROLLED_BACK = "rolled_back";
export const CREATE_ATTACH_ORPHANED = "orphaned";

export class CreateAndAttachError extends Error {
  constructor(kind, message, responsibility) {
    super(message);
    this.name = "CreateAndAttachError";
    this.kind = kind;
    this.responsibility = responsibility;
  }
}

const isTransient = (error) => {
  const status = error?.response?.status;
  return !status || status >= 500 || status === 408 || status === 429;
};

const tryAttach = async (accountId, roleId, responsibilityId) => {
  const payload = { attachment_id: responsibilityId };
  try {
    await attachResponsibilityToRole(accountId, roleId, payload);
    return null;
  } catch (firstError) {
    if (!isTransient(firstError)) {
      return firstError;
    }
    try {
      await attachResponsibilityToRole(accountId, roleId, payload);
      return null;
    } catch (retryError) {
      return retryError;
    }
  }
};

// Returns "attached" | "attached-elsewhere" | "unattached" | "unknown".
const checkAttachment = async (accountId, roleId, responsibilityId) => {
  try {
    const response = await getResponsibilityAttachments(accountId, responsibilityId);
    const roles = response?.data?.results || [];
    if (roles.some((role) => String(role.id) === String(roleId))) {
      return "attached";
    }
    return roles.length ? "attached-elsewhere" : "unattached";
  } catch {
    return "unknown";
  }
};

const describeResponsibility = (responsibility) =>
  responsibility.code_str || `#${responsibility.id}`;

const orphanedError = (responsibility, cause) => {
  const error = new CreateAndAttachError(
    CREATE_ATTACH_ORPHANED,
    `Responsibility ${describeResponsibility(
      responsibility
    )} was created but could not be attached to this role. Attach it from the role's responsibilities, then dismiss this suggestion.`,
    responsibility
  );
  error.cause = cause;
  return error;
};

/**
 * Two-step, non-atomic write: create the Responsibility, then attach it to the role.
 * If the attach fails it is retried once (transient failures only); if it still fails the
 * attachment is re-checked on the server before the orphaned Responsibility is deleted, so a
 * lost response never removes a Responsibility that was in fact attached.
 * Resolves with the create response; rejects with CreateAndAttachError once the create
 * succeeded but the attach did not, or with the original error if the create itself failed.
 */
export const createAndAttachResponsibility = async ({ accountId, roleId, payload }) => {
  const created = await createResponsibility(accountId, payload);
  const responsibility = created.data;

  const attachError = await tryAttach(accountId, roleId, responsibility.id);
  if (!attachError) {
    return created;
  }

  const state = await checkAttachment(accountId, roleId, responsibility.id);
  if (state === "attached") {
    return created;
  }
  if (state !== "unattached") {
    throw orphanedError(responsibility, attachError);
  }

  try {
    await deleteResponsibility(accountId, responsibility.id);
  } catch {
    throw orphanedError(responsibility, attachError);
  }
  const error = new CreateAndAttachError(
    CREATE_ATTACH_ROLLED_BACK,
    "The responsibility could not be attached to this role, so it was not saved. Please try again.",
    responsibility
  );
  error.cause = attachError;
  throw error;
};
